import { useEffect, useRef, useState, useMemo, useCallback, forwardRef, useImperativeHandle } from 'react'
import { useDroppedLoadReask } from '@papan/core-ui/ui/useDroppedLoadReask'
import * as Y from 'yjs'
import { Bug, Code, Loader2, Play } from 'lucide-react'
import { useYjs } from '../client/useYjs.js'
import { useFileIO } from '@papan/files/client/file-io'
import { DebugLogViewer } from '@papan/files/ui'
import { TextModifier, type TextModifierHandle } from './TextModifier.js'
import { writeEditorChange, crToLf, toLf } from './write-editor-change.js'
import { trackTextChanges } from './ytext-change-log.js'
import { revealPending } from './codemirror/CodeMirrorModifier.js'
import { useSaveWithFormat } from './format/useSaveWithFormat.js'
import { useFormatConfig } from './format/useFormatConfig.js'
import { useAppCodeContext } from './code-intel/useAppCodeContext.js'
import { CollabStatusBadge } from './CollabStatusBadge.js'
import { EditorShareMenu } from './EditorShareMenu.js'
import { EditorToolbar } from './EditorToolbar.js'
import { ModeToggle, type ModeOption } from './ModeToggle.js'
import { useCollabText, useLatestCollabText } from './useCollabText.js'
import { ConflictOverlay } from './ConflictOverlay.js'
import { ForbiddenOverlay } from './ForbiddenOverlay.js'
import { ScriptRunPanel } from './ScriptRunPanel.js'
import { ReadOnlyBadge } from './ReadOnlyBadge'
import { FileStateBanner } from './FileStateBanner'
import 'highlight.js/styles/github-dark.css'

// EditorEditHandle is defined in the client editor-registry (canonical location)
import type { EditorEditHandle, EditorUndoRedo, RevealLineRequest } from '../client/editor-registry.js'
import { useYTextUndo, textUndoShortcut } from './useYTextUndo.js'
import { useSaveRefusalToast } from './useSaveRefusalToast.js'
import { errorText } from '@papan/api-client'
export type { EditorEditHandle }

// The src surface deliberately reuses MarkdownEditor's proven Yjs +
// TextModifier wiring (diff-based CRDT edits, save/discard relay,
// conflict overlay). The only new thing here is the Src ↔ Run toggle
// and the Run panel — per docs/plans/2026-05-15_STANDALONE_SCRIPT_RUNNER.md.

interface ScriptEditorProps {
  /** #249: show this line (a chat link `file:42`) — switches to the source view first. */
  revealLine?: RevealLineRequest
  room: string
  fileId: string
  fileName?: string
  parentId?: string | null
  isActive?: boolean
  onDirtyChange?: (dirty: boolean) => void
  onSaveRef?: React.MutableRefObject<(() => void) | null>
  onDiscardRef?: React.MutableRefObject<(() => void) | null>
  /** Claim the app's undo scope so Ctrl+Z can't fall through to the
   *  drive's file-operation stack. See useUndoScopeGuard. */
  registerUndoRedo?: (editor: EditorUndoRedo) => () => void
  /** Initial view mode override (persisted across tab/version switches) */
  initialViewMode?: 'src' | 'run' | string
  onViewModeChange?: (mode: string) => void
  scrollRef?: Record<string, number>
  viewSync?: Record<string, Record<string, unknown>>
  scrollSyncTrigger?: number
  /** Permission-based read-only: live Y.Doc sync but no editing */
  readOnly?: boolean
  onConflictChange?: (conflicted: boolean) => void
  /**
   * Hide the Run panel entirely. Used by share-view to deny recipients
   * the ability to execute the owner's script in the owner's tenant —
   * the share-link grant covers read/edit, not server-side execution.
   * Default false (Drive context shows the Run pill).
   */
  hideRun?: boolean
}

type EditorMode = 'src' | 'run'

const HIGHLIGHT_MAP: Record<string, string> = {
  py: 'python', sh: 'bash', bash: 'bash', zsh: 'bash',
}

const normalizeMode = (m?: string): EditorMode => (m === 'run' ? 'run' : 'src')

export const ScriptEditor = forwardRef<EditorEditHandle, ScriptEditorProps>(function ScriptEditor(
  { room, fileId, fileName, parentId, isActive, onDirtyChange, onConflictChange, onSaveRef, onDiscardRef, registerUndoRedo, initialViewMode, onViewModeChange, scrollRef, viewSync, scrollSyncTrigger, readOnly: readOnlyProp, hideRun, revealLine },
  ref,
) {
  // Local mode: no room — load bytes via FileIO into a fresh local Y.Doc.
  // Used by snapshot share-link recipients. Mirrors MarkdownEditor /
  // HtmlEditor's localMode pattern; the editor surface (TextModifier,
  // syntax highlighting, view-mode toggle) is identical either way.
  const fileIO = useFileIO()
  const localMode = !room

  const [localDoc, setLocalDoc] = useState<Y.Doc | null>(null)
  const [localLoadError, setLocalLoadError] = useState<string | null>(null)
  // #1316: a local load whose answer was dropped (its session ended) is asked again as the session now here
  // Review round 2: in the share view an end that MOVES the share session leaves the ended sid in use until the new
  // session lands — the re-ask waits for it there (`useDroppedLoadReask`), never asking as the ended session
  const { reask: localReask, begin: beginLocalLoad } = useDroppedLoadReask()

  const yjsResult = useYjs({ room: localMode ? '' : room, active: isActive })
  // #823: moved to the bin / being deleted — the last text stays, read-only, under a banner saying why
  const fileState = localMode ? null : yjsResult.fileState
  const readOnly = readOnlyProp || !!fileState
  // A refused save (full drive, frozen tenant) has no route to surface
  // through — see useSaveRefusalToast.
  useSaveRefusalToast(yjsResult.saveError)

  useEffect(() => {
    const asked = beginLocalLoad()   // the share session this load goes out as
    if (!localMode) return
    let cancelled = false
    let createdDoc: Y.Doc | null = null
    setLocalLoadError(null)
    void (async () => {
      try {
        const blob = await fileIO.loadContent(fileId)
        const text = await blob.text()
        if (cancelled) return
        createdDoc = new Y.Doc()
        createdDoc.getText('content').insert(0, text)
        setLocalDoc(createdDoc)
      } catch (err) {
        if (cancelled) return
        const text = errorText(err, 'Failed to load file')
        if (text === null) asked.dropped()
        else setLocalLoadError(text)
      }
    })()
    return () => {
      cancelled = true
      if (createdDoc) createdDoc.destroy()
      setLocalDoc(null)
    }
  }, [localMode, fileId, fileIO, localReask, beginLocalLoad])

  const doc = localMode ? localDoc : yjsResult.doc
  const connected = localMode ? !!localDoc : yjsResult.connected
  const synced = localMode ? !!localDoc : yjsResult.synced
  const isDirty = localMode ? false : yjsResult.isDirty
  const isConflicted = localMode ? false : yjsResult.isConflicted
  const conflictGeneration = localMode ? 0 : yjsResult.conflictGeneration
  const forbidden = localMode ? false : yjsResult.forbidden
  // Reconnect forced a fresh Y.Doc (epoch changed) — keep last content painted
  // + show a "reconnecting" indicator until resync, instead of flashing empty.
  const reloading = localMode ? false : yjsResult.reloading
  const gaveUp = localMode ? false : yjsResult.gaveUp
  const save = localMode
    ? (() => {
        if (!localDoc) return
        const text = localDoc.getText('content').toString()
        void fileIO.saveContent(fileId, text).catch((err) => {
          // #1316: a dropped answer says nothing (the save may have landed; nothing failed for the person now here)
          const msg = errorText(err, 'Save failed')
          if (msg !== null) setLocalLoadError(msg)
        })
      })
    : yjsResult.save
  const forceSave = localMode ? (() => {}) : yjsResult.forceSave
  const discard = localMode ? (() => {}) : yjsResult.discard

  const [initializing, setInitializing] = useState(true)
  // Debug log — run stderr teed by the worker. Author/editor surface only:
  // hidden alongside Run for share-view recipients (`hideRun`) and for
  // localMode snapshots (no live file behind the view).
  const [showDebugLog, setShowDebugLog] = useState(false)
  const [mode, _setMode] = useState<EditorMode>(normalizeMode(initialViewMode))
  const setMode = useCallback((m: EditorMode) => {
    _setMode(m)
    onViewModeChange?.(m)
  }, [onViewModeChange])
  useEffect(() => {
    if (initialViewMode != null) _setMode(normalizeMode(initialViewMode))
  }, [initialViewMode])
  // #249: a line to show is in the source.
  useEffect(() => {
    // Only for a request still to happen: a remount with the same (used) request must not flip the view back.
    if (revealPending(revealLine)) setMode('src')
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per request
  }, [revealLine?.seq])

  const codeApp = useAppCodeContext(fileId)
  const highlightRef = useRef<TextModifierHandle>(null)
  const rawExt = (fileName || fileId).split('.').pop()?.toLowerCase() || 'sh'
  // Issue #157 v2: a `.shared`/`.chain` pointer file carries JSON metadata as its
  // content, so label + syntax-highlight it as JSON — not the misleading
  // "SHARED"/"CHAIN" extension (a demoted or hand-made `*.shared` file is a
  // plain JSON file, and even a real pointer's body is just its metadata JSON).
  const fileExt = (rawExt === 'shared' || rawExt === 'chain') ? 'json' : rawExt
  const highlightLang = HIGHLIGHT_MAP[fileExt]
  // A pointer row (`job.py.shared`) must not offer Run: execution would
  // target the recipient-side pointer/tree, not the source script —
  // run-through-mount is #327 (Track A). Same affordance denial share-view
  // applies via the hideRun prop; the name-keyed check covers the Drive
  // hosts (TabContent / SingleFileView), which don't pass hideRun.
  const runHidden = hideRun || rawExt === 'shared' || rawExt === 'chain'

  const ytext = useMemo(() => doc?.getText('content') ?? null, [doc])

  // Real undo/redo. A read-only mount registers nothing, so Ctrl+Z reaches
  // the drive's file operations instead of dying here.
  // The Y.Text's own change log, for rebasing a keystroke over a collaborator's edit the editor hasn't drawn yet.
  useEffect(() => (ytext ? trackTextChanges(ytext) : undefined), [ytext])
  const textUndo = useYTextUndo(doc, ytext, registerUndoRedo, {
    enabled: !readOnly,
    suspended: isConflicted || reloading,
    bulkUpdating: yjsResult.bulkUpdating,
    bulkUpdatingRef: yjsResult.bulkUpdatingRef,
  })

  // Y.Text → React state, reload-aware (shared across the text editors).
  const content = useCollabText(ytext, reloading)
  const readLatestText = useLatestCollabText(ytext, reloading, content)

  useEffect(() => {
    if (synced) setInitializing(false)
  }, [synced])

  useEffect(() => {
    onDirtyChange?.(isDirty)
  }, [isDirty, onDirtyChange])

  // #987: Format on save (Settings → Editor) formats first; off, it is the plain save.
  const saveWithFormat = useSaveWithFormat(save, highlightRef)
  const loadFormatConfig = useFormatConfig(fileId, fileName)
  useEffect(() => {
    if (onSaveRef) onSaveRef.current = saveWithFormat
    return () => { if (onSaveRef) onSaveRef.current = null }
  }, [saveWithFormat, onSaveRef])

  useEffect(() => {
    if (onDiscardRef) onDiscardRef.current = discard
    return () => { if (onDiscardRef) onDiscardRef.current = null }
  }, [discard, onDiscardRef])

  // Minimal-diff CRDT edits (common prefix/suffix) — same as MarkdownEditor
  const handleChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (!doc || !ytext) return
    writeEditorChange(doc, ytext, e, { stopCapturing: textUndo.stopCapturing })
  }, [doc, ytext, textUndo])

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Route the textarea's Ctrl/Cmd+Z to the SAME history the rest of the app
    // uses. The browser's native textarea undo is not a usable fallback here:
    // TextModifier reassigns `textarea.value` whenever the document changes
    // underneath it, which discards that history without a trace.
    // textUndoShortcut owns which keys count: Ctrl/Cmd+Z, Ctrl/Cmd+Shift+Z and
    // Ctrl+Y (the Windows redo, which fell through to nothing here), and never
    // a chord with Alt held — Windows AltGr is Ctrl+Alt, so AltGr+Z is ż (#829).
    const step = textUndoShortcut(e)
    if (step) {
      e.preventDefault()
      void (step === 'redo' ? textUndo.redo() : textUndo.undo())
      return
    }
    if (e.key === 'Tab') {
      e.preventDefault()
      if (!doc || !ytext) return
      const textarea = e.currentTarget
      const start = textarea.selectionStart
      const end = textarea.selectionEnd
      doc.transact(() => {
        if (end > start) ytext.delete(start, end - start)
        ytext.insert(start, '  ')
      })
      requestAnimationFrame(() => {
        textarea.selectionStart = textarea.selectionEnd = start + 2
      })
    }
  }, [doc, ytext, textUndo])

  useImperativeHandle(ref, () => ({
    copy: () => {
      const ta = highlightRef.current?.textarea
      if (ta) { ta.focus(); document.execCommand('copy') }
    },
    cut: () => {
      const ta = highlightRef.current?.textarea
      if (ta) { ta.focus(); document.execCommand('cut') }
    },
    paste: () => {
      const ta = highlightRef.current?.textarea
      if (!ta) return
      ta.focus()
      navigator.clipboard.readText().then(text => {
        if (!doc || !ytext) return
        const start = ta.selectionStart
        const end = ta.selectionEnd
        const lf = toLf(text)
        doc.transact(() => {
          crToLf(ytext) // the caret is an LF offset (CodeMirror's)
          if (end > start) ytext.delete(start, end - start)
          if (lf) ytext.insert(start, lf)
        })
        requestAnimationFrame(() => {
          ta.selectionStart = ta.selectionEnd = start + lf.length
        })
      }).catch(() => {
        document.execCommand('paste')
      })
    },
    delete: () => {
      const ta = highlightRef.current?.textarea
      if (!ta) return
      ta.focus()
      const start = ta.selectionStart
      const end = ta.selectionEnd
      if (start !== end && doc && ytext) {
        doc.transact(() => { crToLf(ytext); ytext.delete(start, end - start) })
      }
    },
    getSelection: () => {
      const ta = highlightRef.current?.textarea
      if (!ta) return null
      const { selectionStart, selectionEnd, value } = ta
      if (selectionStart === selectionEnd) return null
      const text = value.substring(selectionStart, selectionEnd)
      if (text.length > 10000) return null
      const startLine = value.substring(0, selectionStart).split('\n').length
      const endLine = value.substring(0, selectionEnd).split('\n').length
      return { text, fileName: fileName || '', filePath: '', fileId, startLine, endLine, kind: 'lines' as const }
    },
    focusEditor: () => {
      highlightRef.current?.focusEditor?.()
    },
    format: () => { void highlightRef.current?.format?.() },
  }), [doc, ytext, fileName, fileId])

  if (forbidden) return <ForbiddenOverlay />

  if (initializing && !synced) {
    return (
      <div className="flex-1 flex items-center justify-center themed-text-muted">
        <Loader2 className="animate-spin mr-2" size={20} />
        <span>Connecting...</span>
      </div>
    )
  }

  return (
    <ConflictOverlay
      isConflicted={isConflicted}
      conflictGeneration={conflictGeneration}
      fileId={fileId}
      onDiscard={discard}
      onOverwrite={forceSave}
      onConflictChange={onConflictChange}
      language={highlightLang}
      scrollRef={scrollRef}
      viewSync={viewSync}
      viewSyncId={`${fileId}:edit`}
      scrollSyncTrigger={scrollSyncTrigger}
      editorTextareaRef={highlightRef}
    >
      <FileStateBanner state={fileState} />
      <EditorToolbar
        onClick={e => {
          if ((e.target as HTMLElement).closest('button, a, input, select')) return
          if (mode === 'src') highlightRef.current?.focusEditor?.()
        }}
        mode={
          <ModeToggle
            options={[
              { value: 'src', label: 'Src', icon: <Code size={14} /> },
              /* Share-view recipients don't get the Run affordance — the
                 share-link grant covers read/edit, not server-side script
                 execution in the owner's tenant. */
              ...(!runHidden ? [{ value: 'run', label: 'Run', icon: <Play size={14} /> } satisfies ModeOption<EditorMode>] : []),
            ]}
            value={mode}
            onChange={setMode}
          />
        }
        status={
          <>
            {!runHidden && !localMode && (
              <button
                onClick={() => setShowDebugLog(true)}
                className="themed-icon rounded p-1 hover:themed-bg-tertiary"
                data-tooltip="Debug log"
                aria-label="Debug log"
              >
                <Bug size={14} />
              </button>
            )}
            <EditorShareMenu fileId={fileId} fileName={fileName} />
            <CollabStatusBadge connected={connected} reloading={reloading} localMode={localMode} gaveUp={gaveUp} />
            {readOnly && (
              <ReadOnlyBadge />
            )}
            {localLoadError && (
              <span
                className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/20 text-red-400"
                title={localLoadError}
              >
                {localLoadError.length > 40 ? `${localLoadError.slice(0, 37)}…` : localLoadError}
              </span>
            )}
          </>
        }
      />

      {/* hideRun gates execution server-side via the share-link grant
          shape, but a persisted view mode of 'run' could carry over from
          a previous Drive-context session. Force Src when Run is hidden
          (incl. the pointer-row case in runHidden). */}
      {mode === 'run' && !runHidden ? (
        <ScriptRunPanel fileId={fileId} fileName={fileName} parentId={parentId} />
      ) : (
        <TextModifier
          ref={highlightRef}
          value={content}
          readLatestValue={readLatestText}
          language={highlightLang}
          isActive={isActive}
          onChange={(readOnly || isConflicted || reloading) ? undefined : handleChange}
          onKeyDown={(readOnly || isConflicted || reloading) ? undefined : handleKeyDown}
          mode={readOnly || isConflicted || reloading ? 'readonly' : 'edit'}
          scrollKey={fileId}
          scrollRef={scrollRef}
          viewSync={viewSync}
          viewSyncId={`${fileId}:edit`}
          scrollSyncTrigger={scrollSyncTrigger}
          codeIntel={{ fileName: fileName ?? '', app: codeApp }}
          revealLine={revealLine}
          loadFormatConfig={loadFormatConfig}
        />
      )}

      {showDebugLog && (
        <DebugLogViewer
          file={{ id: fileId, name: fileName ?? 'script', isFolder: false }}
          onClose={() => setShowDebugLog(false)}
        />
      )}
    </ConflictOverlay>
  )
})
